import React from "react";
import _get from "lodash.get";
import { withRouter } from "react-router-dom";

import Stack from "components/common/Stack";
import Loader from "components/common/Loader";
import JarvisCard from "components/JarvisCard";
import BillingHistoryPage from "components/allInOneDashboard/profile/BillingHistoryPage";
import ManageSubscriptionPage from "components/allInOneDashboard/profile/ManageSubscriptionPage";
import PlanAndPricingContainer from "containers/PlanAndPricing";
import { config } from "config";

const initialFilterData = {
  fromDate: null,
  toDate: null,
  transactionStatus: null,
};

const BILLING_SCOPE = "billing";
const screenTypes = {
  VIEW_PROFILE: "VIEW_PROFILE",
  SUBSCRIPTION_PLANS: "SUBSCRIPTION_PLANS",
};

class SubscriptionDetailsContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filterData: JSON.parse(JSON.stringify(initialFilterData)),
      page: 1,
      limit: 25,
      activeScreenType: screenTypes.VIEW_PROFILE,
      isPlanUpgraded: false,
      // Matches Redux rows to Manage vs Billing. null = in-flight / invalidated
      // so product-scoped Manage data cannot flash on Billing History.
      listScope: null,
    };
  }

  /** Manage only when route state carries productCode — never localStorage. */
  getRouteProductCode = () =>
    _get(this.props, "location.state.productCode", "") || "";

  getListScope = (productCode = this.getRouteProductCode()) =>
    productCode ? `manage:${productCode}` : BILLING_SCOPE;

  componentDidMount() {
    const productCode = this.getRouteProductCode();

    if (productCode) {
      this.bootstrapManageMode(productCode);
    } else {
      this.fetchTransactionList({ invalidate: true });
    }
  }

  componentDidUpdate(prevProps) {
    const prevCode = _get(prevProps, "location.state.productCode", "") || "";
    const nextCode = this.getRouteProductCode();

    if (prevCode !== nextCode) {
      this.setState(
        {
          page: 1,
          filterData: JSON.parse(JSON.stringify(initialFilterData)),
          activeScreenType: screenTypes.VIEW_PROFILE,
          isPlanUpgraded: false,
          listScope: null,
        },
        () => {
          if (nextCode) {
            this.bootstrapManageMode(nextCode);
          } else {
            this.fetchTransactionList({ invalidate: true });
          }
        },
      );
      return;
    }

    // Commit scope only after the request finishes so stale Redux rows stay hidden.
    if (
      prevProps.transactionHistoryLoading &&
      !this.props.transactionHistoryLoading &&
      this.state.listScope === null
    ) {
      this.setState({ listScope: this.getListScope() });
    }
  }

  bootstrapManageMode = (productCode) => {
    const {
      reasonsDropdown,
      profile,
      productSubscriptionDetails,
      subscriptionPlans,
      reasonsDropdownAction,
      getProfileDataAction,
      getSubscriptionPlansAction,
      getProductsSubscriptionDetailsAction,
    } = this.props;

    if (!reasonsDropdown?.length) {
      reasonsDropdownAction();
    }

    if (!profile || !Object.keys(profile).length) {
      getProfileDataAction();
    }

    if (!productSubscriptionDetails?.length) {
      getProductsSubscriptionDetailsAction({ productCode });
    }

    const hasPlansForProduct = subscriptionPlans?.some((plan) =>
      plan?.productMap?.some((product) => product.productCode === productCode),
    );

    if (
      !hasPlansForProduct &&
      !this.props.isSubscriptionPlansLoading
    ) {
      getSubscriptionPlansAction({ productCode });
    }

    this.fetchTransactionList({ invalidate: true });
  };

  fetchTransactionList = ({ invalidate = false } = {}) => {
    const {
      page,
      limit,
      filterData: { fromDate, toDate, transactionStatus },
    } = this.state;

    const productCode = this.getRouteProductCode();
    const nextScope = this.getListScope(productCode);
    const shouldInvalidate =
      invalidate || this.state.listScope !== nextScope;

    const dispatchFetch = () => {
      this.props.transactionHistoryAction({
        ...(productCode ? { productCode } : {}),
        page,
        limit,
        transactionStatus,
        fromDate,
        toDate,
      });
    };

    if (shouldInvalidate) {
      this.setState({ listScope: null }, dispatchFetch);
    } else {
      dispatchFetch();
    }
  };

  handleFilterChange = ({ name = null, value = "" }) => {
    this.setState(
      (prev) => ({
        page: 1,
        filterData: {
          ...prev.filterData,
          [name]: value,
        },
      }),
      () => {
        this.fetchTransactionList();
      },
    );
  };

  handleClearFilters = () => {
    this.setState(
      {
        page: 1,
        filterData: JSON.parse(JSON.stringify(initialFilterData)),
      },
      () => {
        this.fetchTransactionList();
      },
    );
  };

  handleReplaceFilters = (filterData = {}) => {
    this.setState(
      {
        page: 1,
        filterData: {
          fromDate: filterData.fromDate ?? null,
          toDate: filterData.toDate ?? null,
          transactionStatus: filterData.transactionStatus ?? null,
        },
      },
      () => {
        this.fetchTransactionList();
      },
    );
  };

  handlePageChange = (page, limit) => {
    this.setState({ page, limit }, () => {
      this.fetchTransactionList();
    });
  };

  isFilterApplied = () => {
    const { filterData } = this.state;
    return !!(
      filterData.fromDate ||
      filterData.toDate ||
      filterData.transactionStatus
    );
  };

  handleBackToProfile = () => {
    this.props.history.push("/customer/profile");
  };

  handlePlanAndPricingBack = () => {
    this.setState({
      activeScreenType: screenTypes.VIEW_PROFILE,
      isPlanUpgraded: false,
    });
  };

  getSubscriptionDetails = (productCode) => {
    const list = this.props.productSubscriptionDetails || [];
    return list.find((item) => item.productCode === productCode) || {};
  };

  getPlanDetails = (planId) => {
    const plans = this.props.subscriptionPlans || [];
    return plans.find((item) => Number(item.planId) === Number(planId)) || {};
  };

  loadPortfolioRenewalPricing = () => {
    const productCode = this.getRouteProductCode();

    if (
      productCode === config.productCodes.PORTFOLIO &&
      !Object.keys(this.props.renewalPricingDetails || {}).length
    ) {
      this.props.getRenewSubscriptionAction();
    }
  };

  handlePlanUpgrade = () => {
    this.loadPortfolioRenewalPricing();
    this.setState({
      activeScreenType: screenTypes.SUBSCRIPTION_PLANS,
      isPlanUpgraded: true,
    });
  };

  handlePlanRenew = () => {
    this.loadPortfolioRenewalPricing();
    this.setState({
      activeScreenType: screenTypes.SUBSCRIPTION_PLANS,
      isPlanUpgraded: false,
    });
  };

  getScopedTransactions = () => {
    const expectedScope = this.getListScope();
    const { listScope } = this.state;
    const {
      transactionHistory = [],
      transactionHistoryCount = 0,
      transactionHistoryLoading,
    } = this.props;

    if (listScope !== expectedScope) {
      return {
        rows: [],
        count: 0,
        loading: true,
      };
    }

    return {
      rows: transactionHistory,
      count: transactionHistoryCount,
      loading: transactionHistoryLoading,
    };
  };

  renderLoader = () => (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "40vh", py: 6, position: "relative" }}
    >
      <Loader position="relative" loading />
    </Stack>
  );

  renderPlanSelection = (productCode) => {
    const subscriptionDetails = this.getSubscriptionDetails(productCode);

    return (
      <JarvisCard sx={{ padding: "20px 30px", background: "unset" }}>
        <PlanAndPricingContainer
          productCode={productCode}
          currentPortfolioCount={subscriptionDetails.portfolioLimitUsage}
          currentStockCount={subscriptionDetails.stockLimitUsage}
          investmentAmount={this.props.renewalPricingDetails?.investmentAmount}
          paymentType={this.state.isPlanUpgraded ? "UPGRADE" : "RENEWAL"}
          selectedPlanId={subscriptionDetails.planId}
          handleScreenChange={this.handlePlanAndPricingBack}
          expiryDate={subscriptionDetails.expiryDate}
          isOnetime={productCode === config.productCodes.PRIME}
          oneTimePayment={productCode === config.productCodes.PRIME}
        />
      </JarvisCard>
    );
  };

  renderManage = (productCode) => {
    const { page, limit } = this.state;
    const {
      profile,
      reasonsDropdown,
      isproductSubscriptionDetailsLoading,
      isSubscriptionPlansLoading,
    } = this.props;

    const subscriptionDetails = this.getSubscriptionDetails(productCode);
    const planDetails = this.getPlanDetails(subscriptionDetails.planId);
    const customerSubscriptionId = _get(
      profile,
      `${productCode}.customerSubscriptionId`,
      "",
    );
    const { rows, count, loading } = this.getScopedTransactions();

    const waitingForDetails =
      isproductSubscriptionDetailsLoading &&
      !Object.keys(subscriptionDetails).length;
    const waitingForPlans =
      isSubscriptionPlansLoading &&
      !Object.keys(planDetails).length;

    const waitingForTransactions = loading && !rows.length && page === 1;

    if (waitingForDetails || waitingForPlans || waitingForTransactions) {
      return this.renderLoader();
    }

    return (
      <ManageSubscriptionPage
        productCode={productCode}
        subscriptionDetails={subscriptionDetails}
        customerSubscriptionId={customerSubscriptionId}
        reasonsDropdown={reasonsDropdown}
        planDetails={planDetails}
        onUpgrade={this.handlePlanUpgrade}
        onRenew={this.handlePlanRenew}
        onBack={this.handleBackToProfile}
        transactionHistory={rows}
        transactionHistoryCount={count}
        transactionHistoryLoading={loading}
        page={page}
        limit={limit}
        onPageChange={this.handlePageChange}
      />
    );
  };

  renderBillingHistory = () => {
    const { filterData, page, limit } = this.state;
    const { transactionStatusFilter: transactionStatusOptions } = this.props;
    const { rows, count, loading } = this.getScopedTransactions();

    if (loading && !rows.length && page === 1) {
      return this.renderLoader();
    }

    return (
      <BillingHistoryPage
        onBack={this.handleBackToProfile}
        filterData={filterData}
        transactionStatusOptions={transactionStatusOptions}
        onFilterChange={this.handleFilterChange}
        onReplaceFilters={this.handleReplaceFilters}
        onClearFilters={this.handleClearFilters}
        isFilterApplied={this.isFilterApplied()}
        transactionHistory={rows}
        transactionHistoryCount={count}
        transactionHistoryLoading={loading}
        page={page}
        limit={limit}
        onPageChange={this.handlePageChange}
      />
    );
  };

  render() {
    const productCode = this.getRouteProductCode();

    if (productCode) {
      if (this.state.activeScreenType === screenTypes.SUBSCRIPTION_PLANS) {
        return this.renderPlanSelection(productCode);
      }

      return this.renderManage(productCode);
    }

    return this.renderBillingHistory();
  }
}

export default withRouter(SubscriptionDetailsContainer);
